import type { PropsWithChildren } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import isNil from "lodash/isNil";
import { Animated } from "react-native";
import {
  SafeAreaView,
  useSafeAreaInsets,
} from "react-native-safe-area-context";

import { AnimationDuration, ToastAnimation } from "@/constants/UI";
import { ToastContext } from "@/hooks/useToast";
import {
  useOrphanToastRequestSelector,
  useToastDispatch,
} from "@/store/reducers/toast";
import type { ToastConfig, ToastRequest } from "@/ui/composed/toast/Toast";
import Toast from "@/ui/composed/toast/Toast";
import { toMs } from "@/utils/date.util";
import { getToastsPositionStyle } from "@/utils/toast.util";

const ToastDisplayDurationMs = toMs({ seconds: 3 });

const ToastProvider = ({ children }: PropsWithChildren) => {
  const [toasts, setToasts] = useState<ToastConfig[]>([]);
  const timers = useRef<Record<string, NodeJS.Timeout>>({});
  const { top } = useSafeAreaInsets();
  const orphanToastRequest = useOrphanToastRequestSelector();
  const { setOrphanToastRequest } = useToastDispatch();

  const animateIn = useCallback(
    ({
      opacityAnim,
      toastId: _toastId,
      translateYAnim,
    }: {
      opacityAnim: Animated.Value;
      toastId: string;
      translateYAnim: Animated.Value;
    }) => {
      Animated.parallel([
        Animated.timing(opacityAnim, {
          duration: AnimationDuration.Default,
          toValue: 1,
          // eslint-disable-next-line @typescript-eslint/naming-convention
          useNativeDriver: true,
        }),
        Animated.timing(translateYAnim, {
          duration: AnimationDuration.Default,
          toValue: 0,
          // eslint-disable-next-line @typescript-eslint/naming-convention
          useNativeDriver: true,
        }),
      ]).start();
    },
    [],
  );

  const animateOut = useCallback(
    ({
      opacityAnim,
      toastId,
      translateYAnim,
    }: {
      opacityAnim: Animated.Value;
      toastId: string;
      translateYAnim: Animated.Value;
    }) => {
      Animated.parallel([
        Animated.timing(opacityAnim, {
          duration: AnimationDuration.Default,
          toValue: 0,
          // eslint-disable-next-line @typescript-eslint/naming-convention
          useNativeDriver: true,
        }),
        Animated.timing(translateYAnim, {
          duration: AnimationDuration.Default,
          toValue: ToastAnimation.TranslateY,
          // eslint-disable-next-line @typescript-eslint/naming-convention
          useNativeDriver: true,
        }),
      ]).start(() => {
        setToasts((prevToasts) =>
          // eslint-disable-next-line sonarjs/no-nested-functions -- currently reported in SonarQube as unresolved
          prevToasts.filter((toast) => toast.id !== toastId),
        );
      });
    },
    [],
  );

  const showToast = useCallback(
    (config: ToastRequest) => {
      const id = Date.now().toString();
      const newToast: ToastConfig = { id, ...config };
      setToasts((prevToasts) => [...prevToasts, newToast]);

      const opacityAnim = new Animated.Value(0);
      const translateYAnim = new Animated.Value(ToastAnimation.TranslateY);

      animateIn({ opacityAnim, toastId: id, translateYAnim });

      // Set timer for auto-dismiss
      timers.current[id] = setTimeout(() => {
        animateOut({ opacityAnim, toastId: id, translateYAnim });
        delete timers.current[id];
      }, ToastDisplayDurationMs);
    },
    [animateIn, animateOut],
  );

  useEffect(() => {
    // orphanToastRequests currently only used for DeepLinking, which could trigger during any UI flow,
    // and thus may not have a persistent React parent component to wait for useToast() to complete
    if (isNil(orphanToastRequest)) return;
    setOrphanToastRequest(undefined);
    showToast(orphanToastRequest);
  }, [orphanToastRequest, setOrphanToastRequest, showToast]);

  // this memoization may seem unnecessary, but this prevents needless rerenders
  // (potentially on every single render) due to the literal object {} being used directly
  const value = useMemo(() => ({ showToast }), [showToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {/* box-none: this absolute top:0 overlay spans the full width of the screen's top
          strip and sits above all app content, so without it the empty container swallows
          taps on whatever is underneath (e.g. the "You" user selector in a screen header).
          box-none lets touches pass through empty areas while actual toasts stay tappable. */}
      <SafeAreaView
        pointerEvents="box-none"
        style={{ position: "absolute", top: 0, width: "100%" }}
      >
        {/* eslint-disable-next-line max-params */}
        {toasts.map((toast, index) => (
          <Toast
            // eslint-disable-next-line react/no-array-index-key
            key={`toast-${index}`}
            positionStyle={getToastsPositionStyle({
              index,
              topSafeAreaInset: top,
              total: toasts.length,
            })}
            toast={toast}
          />
        ))}
      </SafeAreaView>
    </ToastContext.Provider>
  );
};

export { ToastDisplayDurationMs };
export default ToastProvider;
